// Place a library object: it follows the cursor as a faint outline; each click puts one
// down at the (snapped) point, centred there, on the active layer. Stays on for the next
// one — a plant room has more than one pump. Esc or V = done.
import type { Vec } from "../core/geometry";
import type { Doc, Entity } from "../doc/entities";
import { drawEntity, type RenderView } from "../engine/renderEntities";
import { pasteCommand } from "./editOps";
import type { LibraryItem } from "../library/library";
import type { Tool, ToolCtx, ToolKeyEvent, ToolPointerEvent } from "./types";

export class PlaceTool implements Tool {
  readonly id = "place" as const;
  private item: LibraryItem | null = null;
  private cursor: Vec | null = null;
  private placed = 0;
  /** Told after the FIRST object of a run is put down, so the app can bring it into view. */
  onFirstPlaced: ((box: { minX: number; minY: number; maxX: number; maxY: number }) => void) | null = null;

  constructor(private ctx: ToolCtx) {}

  setItem(item: LibraryItem): void {
    this.item = item;
    this.placed = 0;
  }

  activate(): void {
    this.cursor = null;
    this.ctx.setMeasure("none", "");
    this.ctx.setHint(
      this.item ? `Place "${this.item.name}": click to put it down · it stays on for more · Esc = done` : "Pick an object in the Library panel first."
    );
    this.ctx.setCursorTip(this.item ? this.item.name : null);
  }

  deactivate(): void {
    this.cursor = null;
    this.ctx.setCursorTip(null);
    this.ctx.requestRender();
  }

  cancel(): void {
    this.cursor = null;
    this.ctx.requestRender();
  }

  inProgress(): boolean {
    return false;
  }

  pointerDown(e: ToolPointerEvent): void {
    if (e.button !== 0 || !this.item) return;
    const result = pasteCommand({ entities: this.item.entities }, this.ctx.activeLayerId(), e.point);
    if (!result) return;
    this.ctx.store.dispatch(result.cmd);
    // Select the placed object: the top-level ids (a group's children follow the group).
    const top = new Set(result.newIds);
    for (const id of result.newIds) {
      const ent = this.ctx.store.doc.entities[id];
      if (ent?.type === "group") for (const c of ent.children) top.delete(c);
    }
    this.ctx.selection.set([...top]);
    this.placed++;
    if (this.placed === 1) {
      const { w, h } = this.item.size;
      this.onFirstPlaced?.({ minX: e.point.x - w / 2, minY: e.point.y - h / 2, maxX: e.point.x + w / 2, maxY: e.point.y + h / 2 });
    }
    this.ctx.setHint(`Placed ${this.placed} × "${this.item.name}" · click to place another · Esc = done`);
    this.ctx.requestRender();
  }

  pointerMove(e: ToolPointerEvent): void {
    this.cursor = e.point;
    this.ctx.requestRender();
  }

  pointerUp(): void {}

  doubleClick(): void {}

  key(e: ToolKeyEvent): boolean {
    if (e.key === "Escape") {
      this.ctx.switchTool("select");
      return true;
    }
    return false;
  }

  measurement(): boolean {
    return false;
  }

  renderDynamic(v: RenderView): void {
    if (!this.item || !this.cursor) return;
    const at = this.cursor;
    if (this.item.entities.length > 600) {
      // Redrawing thousands of shapes on every mouse move would make the cursor lag:
      // a big object follows the cursor as its outline box.
      const { w, h } = this.item.size;
      v.ctx.save();
      v.ctx.globalAlpha = 0.7;
      v.ctx.setLineDash([6 / v.cam.scale, 4 / v.cam.scale]);
      v.ctx.lineWidth = 1.5 / v.cam.scale;
      v.ctx.strokeStyle = this.ctx.store.doc.defaultStyle.strokeColor;
      v.ctx.strokeRect(at.x - w / 2, at.y - h / 2, w, h);
      v.ctx.restore();
      return;
    }
    // A throwaway document holding the item at the cursor, so groups find their children.
    const ghost: Record<string, Entity> = {};
    for (const src of this.item.entities) {
      const e = structuredClone(src);
      shift(e, at);
      ghost[e.id] = e;
    }
    const doc = this.ctx.store.doc;
    const ghostDoc: Doc = { ...doc, entities: ghost };
    const childIds = new Set(Object.values(ghost).flatMap((e) => (e.type === "group" ? e.children : [])));
    v.ctx.save();
    v.ctx.globalAlpha = 0.55;
    for (const e of Object.values(ghost)) {
      if (childIds.has(e.id)) continue;
      drawEntity(v, e, doc.layers.find((l) => l.id === this.ctx.activeLayerId()), ghostDoc);
    }
    v.ctx.restore();
  }
}

function shift(e: Entity, d: Vec): void {
  const mv = (p: Vec): Vec => ({ x: p.x + d.x, y: p.y + d.y });
  switch (e.type) {
    case "polyline":
    case "marker":
      e.points = e.points.map(mv);
      break;
    case "circle":
    case "arc":
      e.center = mv(e.center);
      break;
    case "text":
    case "image":
    case "grid":
      e.pos = mv(e.pos);
      break;
    case "label":
      e.head = mv(e.head);
      e.tail = mv(e.tail);
      break;
    case "dimension":
      for (const a of [e.a, e.b]) if (a.kind === "free") a.p = mv(a.p);
      break;
    case "group":
      break;
  }
}
